import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, ButtonGroup, Divider, Heading, Icon, Modal, Tag, TextButton } from '@zillow/constellation-10';
import { IconAIMagicFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyDetailsWebApiHandler_details } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import {
  FieldEditModalHeaderActionProvider,
  useFieldEditModalHeaderAction,
} from '../../../contexts/FieldEditModalHeaderActionContext';
import { FormModeProvider, useFormMode } from '../../../contexts/FormModeContext';

import RevertButton from '../../RevertButton';

import type { ReactNode } from 'react';

type FieldEditModalProps = {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  isSaveDisabled?: boolean;
  saveLabel?: string;
  isAiAssisted?: boolean;
};

const FieldEditModalContent = ({
  title,
  onClose,
  children,
  isSaveDisabled,
  saveLabel = 'Save',
  isAiAssisted = false,
}: Omit<FieldEditModalProps, 'isOpen'>) => {
  const [isSaving, setIsSaving] = useState(false);
  const formModeContext = useFormMode();
  const router = useRouter();
  const queryClient = useQueryClient();

  const listingAlias = router.query.listingAlias as string;

  const { headerAction } = useFieldEditModalHeaderAction();

  const handleSave = async () => {
    if (isSaving) return;

    try {
      setIsSaving(true);

      if (formModeContext.mode === 'modal') {
        const { hasValidationErrors, hasMutationErrors, savesExecuted } = await formModeContext.triggerAllSaves();

        // Don't close - let user fix errors
        if (hasValidationErrors || hasMutationErrors) {
          return;
        }

        // Only wait for refetch if saves were actually executed
        if (savesExecuted > 0 && listingAlias) {
          // Get the query key for listingDetails
          const listingDetailsQueryKey = useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({
            listingAlias,
            includeTopDupesDetails: true,
            includeVrModels: false,
            includeHasVrModels: false,
            includeWebAnalytics: true,
            includeDeliveryMethod: true,
          });

          // Wait for automatic refetch (triggered by meta.invalidates) to complete
          // Poll with a small delay until isFetching becomes false
          while (queryClient.isFetching({ queryKey: listingDetailsQueryKey }) > 0) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
        }
      }

      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal.Portal>
      <Modal.Backdrop>
        <Modal.Content>
          <Modal.Header>
            <Heading level={2}>{title}</Heading>
          </Modal.Header>
          <Divider />
          <Modal.Body>
            {isAiAssisted && (
              <Box css={{ marginBottom: '400' }}>
                <Tag tone="purple" icon={<Icon render={<IconAIMagicFilled />} />}>
                  AI assisted
                </Tag>
              </Box>
            )}
            {children}
          </Modal.Body>
          <Divider />
          <Modal.Footer css={{ display: 'flex', alignItems: 'center', gap: '400' }}>
            {headerAction && <RevertButton ariaLabel={headerAction.ariaLabel} onRevert={headerAction.onAction} />}
            <ButtonGroup aria-label={`${title} actions`} css={{ marginInlineStart: 'auto' }}>
              <TextButton onClick={onClose} disabled={isSaving}>
                Cancel
              </TextButton>
              <Button tone="brand" emphasis="filled" onClick={handleSave} loading={isSaving} disabled={isSaveDisabled}>
                {saveLabel}
              </Button>
            </ButtonGroup>
          </Modal.Footer>
          <Modal.CloseButton />
        </Modal.Content>
      </Modal.Backdrop>
    </Modal.Portal>
  );
};

export const FieldEditModal = ({
  title,
  isOpen,
  onClose,
  children,
  isSaveDisabled,
  saveLabel,
  isAiAssisted,
}: FieldEditModalProps) => {
  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'md' }}
      useDismissProps={{
        outsidePress: false,
      }}
    >
      <FormModeProvider mode="modal">
        <FieldEditModalHeaderActionProvider>
          <FieldEditModalContent
            title={title}
            onClose={onClose}
            isSaveDisabled={isSaveDisabled}
            saveLabel={saveLabel}
            isAiAssisted={isAiAssisted}
          >
            {children}
          </FieldEditModalContent>
        </FieldEditModalHeaderActionProvider>
      </FormModeProvider>
    </Modal.Root>
  );
};
